<template>
  <div class="rcpt-root">

    <!-- ── Page header ───────────────────────────────── -->
    <div class="rcpt-header">
      <div class="rcpt-header-pattern"></div>
      <div class="rcpt-header-inner">
        <div class="rcpt-header-top">
          <v-btn icon small class="rcpt-back-btn" @click="$router.back()">
            <v-icon size="20" color="white">arrow_back</v-icon>
          </v-btn>
        </div>
        <div class="rcpt-header-body">
          <div class="rcpt-header-icon">
            <v-icon size="26" color="white">receipt_long</v-icon>
          </div>
          <div>
            <h2 class="rcpt-title">My Payouts</h2>
            <p class="rcpt-sub">Collection sessions where you received funds</p>
          </div>
        </div>
      </div>
    </div>

    <!-- ── New Feature Info Banner ───────────────────── -->
    <div class="rcpt-info-banner">
      <div class="rcpt-info-icon">
        <v-icon size="22" color="#0288d1">lightbulb</v-icon>
      </div>
      <div class="rcpt-info-content">
        <div class="rcpt-info-title">New Collection History View</div>
        <div class="rcpt-info-text">
          All Njangi payouts recorded by your manager now appear here as official digital receipts.
        </div>
      </div>
    </div>

    <!-- ── Search bar ─────────────────────────────────── -->
    <div v-if="!loading && receipts.length > 0" class="rcpt-search-bar">
      <v-icon size="17" color="grey" class="rcpt-search-icon">search</v-icon>
      <input
        v-model="searchQuery"
        class="rcpt-search-input"
        placeholder="Search by group name"
        type="text"
      />
      <button v-if="searchQuery" class="rcpt-search-clear" @click="searchQuery = ''">
        <v-icon size="15" color="grey">close</v-icon>
      </button>
    </div>

    <!-- ── Loading ────────────────────────────────────── -->
    <div v-if="loading" class="rcpt-state-center">
      <v-progress-circular indeterminate color="primary" size="36"></v-progress-circular>
      <p class="rcpt-state-text">Loading your payouts...</p>
    </div>

    <!-- ── Empty ──────────────────────────────────────── -->
    <div v-else-if="receipts.length === 0" class="rcpt-empty">
      <v-icon size="52" color="grey lighten-2">inbox</v-icon>
      <p class="rcpt-empty-text">No collection sessions found. Your manager has not recorded any payout yet.</p>
    </div>

    <!-- ── No search result ───────────────────────────── -->
    <div v-else-if="filteredReceipts.length === 0" class="rcpt-empty">
      <v-icon size="44" color="grey lighten-2">search_off</v-icon>
      <p class="rcpt-empty-text">No results for "{{ searchQuery }}".</p>
      <button class="rcpt-reset-btn" @click="searchQuery = ''">Clear search</button>
    </div>

    <!-- ── Receipt list ───────────────────────────────── -->
    <div v-else class="rcpt-list">
      <div
        v-for="item in visibleReceipts"
        :key="item._key"
        class="rcpt-card rcpt-card--paid"
      >
        <!-- Top: group + session label + status -->
        <div class="rcpt-card-top">
          <div class="rcpt-card-top-left">
            <div class="rcpt-avatar">{{ initials(item.group_name) }}</div>
            <div>
              <div class="rcpt-group-name">{{ item.group_name }}</div>
              <div class="rcpt-session-label">Session #{{ item.session_number }} &middot; {{ item.cycle_type }}</div>
            </div>
          </div>
          <div class="rcpt-status-badge rcpt-badge--paid">
            <v-icon size="12" color="#00695c" class="mr-1">check_circle</v-icon>
            Paid
          </div>
        </div>

        <!-- Dashed separator -->
        <div class="rcpt-dash"></div>

        <!-- Amount -->
        <div class="rcpt-amount-row">
          <div class="rcpt-amount-label">Amount received</div>
          <div class="rcpt-amount-value rcpt-amount--paid">
            {{ formatAmount(item.amount_paid) }}<span class="rcpt-currency"> FCFA</span>
          </div>
        </div>

        <!-- Details -->
        <div class="rcpt-details">
          <div class="rcpt-detail-row">
            <span class="rcpt-detail-key">
              <v-icon size="12" color="grey" class="mr-1">calendar_today</v-icon>Period
            </span>
            <span class="rcpt-detail-val">
              <template v-if="item.session_start_date && item.session_end_date">
                {{ item.session_start_date }} &rarr; {{ item.session_end_date }}
              </template>
              <span v-else class="rcpt-na">No date defined</span>
            </span>
          </div>

          <div class="rcpt-detail-row">
            <span class="rcpt-detail-key">
              <v-icon size="12" color="grey" class="mr-1">phone_android</v-icon>Method
            </span>
            <span class="rcpt-detail-val">{{ item.payment_method || 'Mobile Money' }}</span>
          </div>
          <div class="rcpt-detail-row">
            <span class="rcpt-detail-key">
              <v-icon size="12" color="grey" class="mr-1">event</v-icon>Paid on
            </span>
            <span class="rcpt-detail-val">{{ item.paid_at || 'N/A' }}</span>
          </div>
          <div v-if="item.recorded_by" class="rcpt-detail-row">
            <span class="rcpt-detail-key">
              <v-icon size="12" color="grey" class="mr-1">person</v-icon>Recorded by
            </span>
            <span class="rcpt-detail-val">{{ item.recorded_by }}</span>
          </div>
        </div>

        <!-- Perforation footer -->
        <div class="rcpt-perforation">
          <div class="rcpt-perf-hole rcpt-perf-hole--left"></div>
          <div class="rcpt-perf-line"></div>
          <div class="rcpt-perf-hole rcpt-perf-hole--right"></div>
        </div>
      </div>

      <!-- ── Show More / Show Less Button ──────────────── -->
      <div v-if="filteredReceipts.length > 5" class="rcpt-more-wrap">
        <button v-if="displayLimit < filteredReceipts.length" class="rcpt-more-btn" @click="displayLimit = filteredReceipts.length">
          <span>Show More ({{ filteredReceipts.length - displayLimit }} remaining)</span>
          <v-icon size="18" color="#1976d2">expand_more</v-icon>
        </button>
        <button v-else class="rcpt-more-btn" @click="displayLimit = 5">
          <span>Show Less</span>
          <v-icon size="18" color="#1976d2">expand_less</v-icon>
        </button>
      </div>

    </div>

  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'MyCollectionHistory',

  data() {
    return {
      loading: true,
      groupsHistory: [],
      searchQuery: '',
      displayLimit: 5,
    }
  },

  computed: {
    receipts() {
      const list = []
      for (const group of this.groupsHistory) {
        for (const sess of group.sessions || []) {
          // Only include paid sessions
          if (!sess.is_paid) continue
          const item = {
            _key: `${group.group_id}-${sess.session_number}`,
            group_id: group.group_id,
            group_name: group.group_name,
            collecting_cycle: group.collecting_cycle,
            cycle_type: sess.cycle_type || group.collecting_cycle,
            session_number: sess.session_number,
            session_start_date: sess.session_start_date,
            session_end_date: sess.session_end_date,
            is_paid: true,
            amount_paid: sess.amount_paid,
            amount_expected: sess.amount_expected,
            payment_method: sess.payment_method,
            paid_at: sess.paid_at,
            recorded_by: sess.recorded_by,
            is_partial: sess.is_partial || false,
          }
          list.push(item)
        }
      }
      list.sort((a, b) => b.session_number - a.session_number)
      return list
    },

    filteredReceipts() {
      if (!this.searchQuery.trim()) return this.receipts
      const q = this.searchQuery.toLowerCase()
      return this.receipts.filter(r => r.group_name.toLowerCase().includes(q))
    },

    visibleReceipts() {
      return this.filteredReceipts.slice(0, this.displayLimit)
    },
  },

  async mounted() {
    document.title = 'PettyCash | My Payouts'
    await this.fetchHistory()
  },

  methods: {
    async fetchHistory() {
      this.loading = true
      try {
        const res = await axios.get('/api/v1/njangi/get/my/collection-history/')
        this.groupsHistory = res.data
        console.log('📋 [MyPayouts] Loaded:', this.groupsHistory.length, 'groups,', this.receipts.length, 'sessions')
      } catch (error) {
        console.error('❌ [MyPayouts] Failed to load:', error)
        this.$store.commit('setSnackBarMessage', 'Unable to load your payout history.')
        this.$store.commit('setSnackBarColor', 'red darken-4')
        this.$store.commit('activateSnackBar', true)
      } finally {
        this.loading = false
      }
    },

    formatAmount(amount) {
      if (amount === null || amount === undefined) return '—'
      return Number(amount).toLocaleString('fr-CM')
    },

    initials(name) {
      if (!name) return '?'
      return name.trim().split(/\s+/).map(w => w.charAt(0).toUpperCase()).slice(0, 2).join('')
    },
  },
}
</script>

<style scoped>
.rcpt-root {
  max-width: 600px;
  margin: 0 auto;
  padding: 0 0 48px;
}

/* Header */
.rcpt-header {
  position: relative;
  background: #00479b;
  overflow: hidden;
}
.rcpt-header-pattern {
  position: absolute;
  inset: 0;
  background-image: radial-gradient(circle, rgba(255,255,255,0.12) 1px, transparent 1px);
  background-size: 22px 22px;
  pointer-events: none;
}
.rcpt-header-inner { position: relative; padding: 12px 14px 18px; }
.rcpt-header-top { display: flex; justify-content: flex-end; margin-bottom: 10px; }
.rcpt-back-btn {
  background: rgba(255,255,255,0.15) !important;
  border: 1px solid rgba(255,255,255,0.25) !important;
  box-shadow: none !important;
}
.rcpt-header-body { display: flex; align-items: center; gap: 14px; }
.rcpt-header-icon {
  width: 50px; height: 50px; border-radius: 14px;
  background: rgba(255,255,255,0.15); border: 1.5px solid rgba(255,255,255,0.25);
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.rcpt-title { font-size: 18px; font-weight: 700; color: #fff; margin: 0; }
.rcpt-sub { font-size: 12px; color: rgba(255,255,255,0.65); margin: 4px 0 0; }

/* Info Banner */
.rcpt-info-banner {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin: 12px 12px 4px;
  padding: 12px 14px;
  background: #e1f5fe;
  border: 1px solid #b3e5fc;
  border-radius: 14px;
}
.rcpt-info-icon {
  flex-shrink: 0;
  margin-top: 1px;
}
.rcpt-info-title {
  font-size: 13px;
  font-weight: 700;
  color: #0277bd;
  margin-bottom: 2px;
}
.rcpt-info-text {
  font-size: 12px;
  color: #01579b;
  line-height: 1.4;
}

/* Search */
.rcpt-search-bar {
  display: flex; align-items: center; background: #fff;
  border-bottom: 1px solid #f0f0f0; padding: 10px 14px;
  gap: 8px; position: sticky; top: 0; z-index: 2;
}
.rcpt-search-icon { flex-shrink: 0; }
.rcpt-search-input { flex: 1; border: none; outline: none; font-size: 14px; color: #1a1a2e; background: transparent; }
.rcpt-search-input::placeholder { color: #bbb; }
.rcpt-search-clear { background: none; border: none; cursor: pointer; padding: 0; display: flex; align-items: center; }

/* States */
.rcpt-state-center { text-align: center; padding: 72px 16px; }
.rcpt-state-text { margin-top: 12px; font-size: 13px; color: #aaa; }
.rcpt-empty {
  text-align: center; padding: 56px 16px; margin: 20px 14px;
  border-radius: 16px; border: 1.5px dashed #e0e0e0; background: #fafafa;
}
.rcpt-empty-text { margin-top: 12px; font-size: 14px; color: #aaa; }
.rcpt-reset-btn {
  margin-top: 12px; font-size: 13px; font-weight: 500; color: #1976d2;
  background: none; border: 1px solid #c5d9f5; border-radius: 20px; padding: 5px 16px; cursor: pointer;
}

/* List */
.rcpt-list { padding: 14px 12px; display: flex; flex-direction: column; gap: 16px; }

/* Card */
.rcpt-card {
  background: #fff; border-radius: 16px; overflow: visible;
  box-shadow: 0 2px 14px rgba(0,0,0,0.07); border: 1px solid #ebebeb;
  transition: box-shadow 0.2s; position: relative;
}
.rcpt-card--paid    { border-left: 4px solid #00897b; }
.rcpt-card--pending { border-left: 4px solid #ffa726; }
.rcpt-card:hover    { box-shadow: 0 4px 20px rgba(0,0,0,0.11); }

/* Card top */
.rcpt-card-top { display: flex; align-items: center; justify-content: space-between; padding: 14px 16px 12px; gap: 10px; }
.rcpt-card-top-left { display: flex; align-items: center; gap: 12px; min-width: 0; }
.rcpt-avatar {
  width: 42px; height: 42px; border-radius: 12px;
  background: #edf3fd; border: 1.5px solid #d0e4fb; color: #1565c0;
  font-size: 13px; font-weight: 700; display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.rcpt-group-name { font-size: 14px; font-weight: 700; color: #1a1a2e; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.rcpt-session-label { font-size: 11px; color: #999; margin-top: 2px; text-transform: uppercase; letter-spacing: 0.4px; }

/* Badge */
.rcpt-status-badge { font-size: 11px; font-weight: 700; padding: 4px 10px; border-radius: 20px; display: flex; align-items: center; flex-shrink: 0; }
.rcpt-badge--paid    { background: #e0f2f1; color: #00695c; }
.rcpt-badge--pending { background: #fff3e0; color: #e65100; }

/* Dashed line */
.rcpt-dash {
  height: 1px; margin: 0 14px;
  background-image: repeating-linear-gradient(to right, #ddd 0, #ddd 6px, transparent 6px, transparent 12px);
}

/* Amount */
.rcpt-amount-row { display: flex; align-items: center; justify-content: space-between; padding: 12px 16px 8px; gap: 8px; }
.rcpt-amount-label { font-size: 11px; text-transform: uppercase; letter-spacing: 0.6px; color: #aaa; font-weight: 600; }
.rcpt-amount-value { font-size: 20px; font-weight: 700; line-height: 1; }
.rcpt-amount--paid    { color: #00695c; }
.rcpt-amount--pending { color: #e65100; }
.rcpt-currency { font-size: 12px; font-weight: 500; color: #888; margin-left: 3px; }
.rcpt-pending-text { font-size: 14px; font-style: italic; color: #e65100; }

/* Details */
.rcpt-details { padding: 4px 16px 12px; display: flex; flex-direction: column; gap: 7px; }
.rcpt-detail-row { display: flex; align-items: flex-start; justify-content: space-between; gap: 8px; font-size: 12px; }
.rcpt-detail-key { color: #aaa; display: flex; align-items: center; flex-shrink: 0; font-weight: 500; min-width: 96px; }
.rcpt-detail-val { color: #1a1a2e; font-weight: 500; text-align: right; word-break: break-word; }
.rcpt-na { color: #ccc; font-style: italic; }

/* Perforation */
.rcpt-perforation { display: flex; align-items: center; height: 18px; background: #f9f9f9; border-top: 1px dashed #e0e0e0; position: relative; overflow: visible; }
.rcpt-perf-hole { width: 20px; height: 20px; border-radius: 50%; background: #f0f2f5; border: 1px solid #ddd; position: absolute; top: 50%; transform: translateY(-50%); }
.rcpt-perf-hole--left  { left: -10px; }
.rcpt-perf-hole--right { right: -10px; }
.rcpt-perf-line {
  flex: 1; height: 1px; margin: 0 14px;
  background-image: repeating-linear-gradient(to right, #ddd 0, #ddd 5px, transparent 5px, transparent 10px);
}

/* Show More Button */
.rcpt-more-wrap {
  display: flex;
  justify-content: center;
  margin-top: 8px;
}
.rcpt-more-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  background: #e3f2fd;
  border: 1px solid #bbdefb;
  color: #1976d2;
  font-size: 13px;
  font-weight: 600;
  padding: 8px 20px;
  border-radius: 24px;
  cursor: pointer;
  transition: background 0.2s, transform 0.1s;
}
.rcpt-more-btn:hover {
  background: #bbdefb;
}
.rcpt-more-btn:active {
  transform: scale(0.98);
}
</style>
